import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  EventEmitter,
  Output,
  Inject
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { SearchControlOptions } from './search-controls.model';

@Component({
  selector: 'app-search-controls',
  templateUrl: './search-controls.component.html',
  styleUrls: ['./search-controls.component.scss']
})
export class SearchControlsComponent implements OnInit {
  @ViewChild('searchButton') searchButton;
  @ViewChild('memberSearchDob') memberSearchDobElementRef: ElementRef;
  @ViewChild('searchField') searchField: ElementRef;
  @Input() options: SearchControlOptions;
  @Input() showActions: boolean = true;
  @Input() searchOnSelection: boolean = false;
  @Input() allowSearchByDefault: boolean = false;
  @Input() isSearchFromPopup: boolean = false;
  @Input() disabled: boolean = false;
  searchForm: UntypedFormGroup;
  idLength = 11;
  inputFieldMaxLenght = 100;

  @Input() typeheadSearchResults: {
    [key: string]: { values: [] };
  };

  @Output() autoCompleteValueChanged = new EventEmitter<{ formControlName: string; formValue: any }>();
  @Output() onSearch = new EventEmitter<any>();
  @Output() onReset = new EventEmitter<boolean>();
  @Output() focused = new EventEmitter<void>();
  @Output() notFocused = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();

  minDate = this.getMinDate();
  validationErrors: any;

  constructor(private formBuilder: UntypedFormBuilder, @Inject(DOCUMENT) private doc: Document) { }

  ngOnInit(): void {
    this.buildSearchForm();
  }

  updateDateInputValidity(event, formControlName): void {
    const errors = !event.target.checkValidity() ? { invalid: true } : undefined;
    this.searchForm.controls[formControlName].setErrors(errors);
  }
  getMinDate(): string {
    return `${new Date().getUTCFullYear() - 125}-01-01`;
  }

  buildSearchForm(): void {
    const formObject: any = {};
    this.options.controls.forEach((control) => {
      formObject[control.formControlName] = [control.initialValue || '', control.validators];
    });
    this.searchForm = this.formBuilder.group(formObject);
  }

  handleTypeaheadSearch(formControlName: string, value: string, emit = true): void {
    this.searchForm?.controls?.[formControlName]?.setValue(value);
    if (emit) {
      this.autoCompleteValueChanged.emit({ formControlName, formValue: this.searchForm.getRawValue() });
    }
  }

  validateInput(): boolean {
    this.notFocused.emit();
    const validationErrors = this.options.validationFunction(this.searchForm);
    const isError = validationErrors.isError;
    this.validationErrors = validationErrors.errors;
    return isError;
  }

  search(): void {
    this.searchButton?.nativeElement.focus();
    if (this.validateInput() || (!Object.values(this.searchForm.value).some(Boolean) && !this.allowSearchByDefault)) {
      return;
    }
    this.onSearch.emit(this.searchForm.value);
  }

  reset(value?: any): void {
    const autoFocusControl = this.options.controls.find(control => control.autofocus);
    if (autoFocusControl) {
      this.doc.getElementById(autoFocusControl.fieldName).focus();
    }
    this.searchForm.reset();
    this.validationErrors = null;
    for (const control in this.typeheadSearchResults) {
      this.typeheadSearchResults[control].values = [];
    }

    if ('setValue' in this.searchField.nativeElement) {
      (this.searchField.nativeElement as any).setValue(value || '');
    }

    this.onReset.next(true);
  }

  focus(): void {
    this.searchField.nativeElement.focus();
  }

  disableControl(controlName: string): void {
    this.searchForm.controls[controlName].disable();
  }

  enableControl(controlName: string): void {
    this.searchForm.controls[controlName].enable();
  }

  isDisabled(controlName: string): boolean {
    return this.searchForm.controls[controlName].disabled;
  }

  cancel() {
    this.reset();
    this.onCancel.emit();
  }
}
